import Box from '@mui/material/Box'
import ModalConfirmComponent from 'components/modal-confirm'
import SelectMobileComponent from 'components/select-mobile'
import SwitchComponent from 'components/switch'
import TextAreaComponent from 'components/textarea'
import { useEffect, useState } from 'react'
import { CreateNotificationProps, NotifyTypes, notifyTypeTitles } from 'types/NotificationTypes'
import { commentTooltip } from 'utils/constants'
import { isValidComments } from 'utils/validations'
import './index.css'

interface NotifyUserComponentProps {
  isShow: boolean
  title: string
  allowableNotifyTypes: NotifyTypes[]
  smsFeature: boolean
  phone: string
  onActionCancel: () => void
  onActionSubmit: (notification: CreateNotificationProps, sendSms?: boolean, sendPush?: boolean) => void
}

const NotifyUserComponent = (props: NotifyUserComponentProps): JSX.Element => {
  const { isShow, title, allowableNotifyTypes, smsFeature, phone, onActionCancel, onActionSubmit } = props
  const [notificationType, setNotificationType] = useState<NotifyTypes>(allowableNotifyTypes[0])
  const [notificationBody, setNotificationBody] = useState<string>('')
  const [sendPush, setSendPush] = useState(true)
  const [sendSMS, setSendSMS] = useState(false)

  // Hooks
  useEffect(() => {
    if (isShow) return // Once modal is opened do not update internal state
    setNotificationType(allowableNotifyTypes[0])
    setNotificationBody('')
    setSendPush(true)
    setSendSMS(false)
  }, [isShow, allowableNotifyTypes])
  useEffect(() => {
    if (notificationType !== NotifyTypes.URGENT_REMINDERS) {
      setSendSMS(false)
    }
  }, [notificationType])

  // Handlers
  const isValid: boolean = isValidComments(notificationBody, true) && allowableNotifyTypes.includes(notificationType)
  const handleNotifyUser = async () => {
    onActionSubmit(
      {
        userIds: phone ? [phone] : [],
        notificationTitle: notifyTypeTitles[notificationType],
        notificationType,
        notificationBody,
      },
      sendSMS,
      sendPush,
    )
  }

  return (
    <ModalConfirmComponent
      title={title}
      actionIcon={'yellowNotifyIcon'}
      isShow={isShow}
      body={
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          textAlign='left'
          gap={2}
          width='100%'
          minWidth='280px'
        >
          {allowableNotifyTypes.length > 1 && (
            <SelectMobileComponent
              data_testid='notification_type_testid'
              label='Type'
              isRequired={true}
              options={allowableNotifyTypes.map((type) => notifyTypeTitles[type])}
              value={notifyTypeTitles[notificationType]}
              onChange={(v: string) => {
                const type = Object.entries(notifyTypeTitles).find(([_, value]) => value === v)?.[0]
                if (type) {
                  setNotificationType(Number(type) as NotifyTypes)
                }
              }}
            />
          )}
          <TextAreaComponent
            labeltext='Message'
            placeholder='Type here...'
            isRequired={true}
            isError={!isValidComments(notificationBody)}
            helperText={!isValidComments(notificationBody) ? commentTooltip() : ''}
            value={notificationBody}
            onChange={(v: string) => setNotificationBody(v)}
          />
          <SwitchComponent size='small' isChecked={sendPush} onChange={() => setSendPush(!sendPush)}>
            Push Notification
          </SwitchComponent>
          {notificationType === NotifyTypes.URGENT_REMINDERS && (
            <SwitchComponent
              size='small'
              disable={!smsFeature}
              isChecked={sendSMS}
              onChange={() => setSendSMS(!sendSMS)}
            >
              {smsFeature ? 'SMS' : 'SMS (Enable Announcement Feature)'}
            </SwitchComponent>
          )}
        </Box>
      }
      actionText={'Send'}
      onActionCancel={onActionCancel}
      onActionSubmit={handleNotifyUser}
      isActionSubmitDisable={!isValid}
    />
  )
}

export default NotifyUserComponent
